import {
	ReactElement,
	Ref,
	useImperativeHandle
} from "react";
import { FrequentlyUsedTileModel } from "client/Api/Workspace/workspace";
import { FrequentlyUsedTileVisualList } from "client/Features/Workspace/FrequentlyUsedTileGroup/FrequentlyUsedTileVisualList";
import {
	generateTrackingId,
	isNotNil
} from "client/Misc/utils";
import { range } from "client_legacy/ndaw/arrayHelpers";
import { FrequentlyUsedTileVisual } from "client/Features/Workspace/FrequentlyUsedTileGroup/FrequentlyUsedTileVisual";
import { FontIconName } from "client/Components";
import { SkeletonFrequentlyUsedTileVisual } from "client/Features/Workspace/FrequentlyUsedTileGroup/SkeletonFrequentlyUsedTileVisual";
import {
	useKeyboardGridNavigation,
	KeyboardGridNavigationContext,
	useKeyboardGridNavigationItem,
	GridNavigationDirection
} from "client/Hooks/useKeyboardGridNavigation";
import { Key, Rect } from "client/Misc/commonTypes";

export type FrequentlyUsedAppsHandle = {
	focusFirstItem: () => void;
};

type FrequentlyUsedAppsProps = {
	ref?: Ref<FrequentlyUsedAppsHandle>;
	tabbableIndex?: number | null;
	onTabbableIndexChange?: (index: number) => void;
	onOutOfBounds?: (
		direction: GridNavigationDirection,
		rect: Rect
	) => void;
} & ({
	status: "loading";
} | {
	status: "ready";
	items: readonly FrequentlyUsedTileModel[];
	onInvoke: (item: FrequentlyUsedTileModel) => void;
});

export const FrequentlyUsedAppList = (props: FrequentlyUsedAppsProps): ReactElement => {
	const {
		ref,
		tabbableIndex,
		onTabbableIndexChange,
		onOutOfBounds,
	} = props;

	const {
		contextData,
		focusItemAtHandler
	} = useKeyboardGridNavigation({
		onFocusChange: (index: number): void => {
			onTabbableIndexChange?.(index);
		},
		onOutOfBounds
	});

	useImperativeHandle(
		ref,
		() => ({
			focusFirstItem: () => {
				focusItemAtHandler("start");
			}
		}),
		[focusItemAtHandler]
	);

	return (
		<FrequentlyUsedTileVisualList>
			{props.status === "loading"
				? range(3).map(
					x => <SkeletonItem key={x}/>
				)
				: (
					<KeyboardGridNavigationContext.Provider value={contextData}>
						{props.items.map(
							(item, index) => (
								<Item
									key={item.tile.id}
									index={index}
									model={item}
									onInvoke={props.onInvoke}
									tabIndex={tabbableIndex === index ? 0 : -1}
								/>
							)
						)}
					</KeyboardGridNavigationContext.Provider>
				)
			}
		</FrequentlyUsedTileVisualList>
	);
};
FrequentlyUsedAppList.displayName = "FrequentlyUsedAppList";

type ItemProps = {
	index: number;
	model: FrequentlyUsedTileModel;
	onInvoke: (item: FrequentlyUsedTileModel) => void;
	tabIndex?: number;
};

const Item = (props: ItemProps): ReactElement => {
	const {
		index,
		model,
		onInvoke,
		tabIndex
	} = props;

	const {
		tile,
		conditionalAccess,
		maintenanceWindow
	} = model;

	const navigationHandler: () => void = () => onInvoke(model);

	const {
		ref,
		onKeyDown
	} = useKeyboardGridNavigationItem({
		index,
		onExecute: navigationHandler,
		executeKey: [Key.Enter, Key.Space]
	});

	const statusIcon: FontIconName | undefined = conditionalAccess
		? "ban"
		: maintenanceWindow
			? "screwdriver-wrench"
			: undefined;

	const isDisabled: boolean = isNotNil(statusIcon);

	return (
		<FrequentlyUsedTileVisualList.Item>
			<FrequentlyUsedTileVisual
				ref={ref}
				trackingId={generateTrackingId("item", `|${tile.id}`)}
				name={tile.name}
				color={tile.color}
				icon={tile.imageDisplayInfo}
				isDisabled={isDisabled}
				statusIcon={statusIcon}
				onClick={navigationHandler}
				onKeyDown={onKeyDown}
				tabIndex={tabIndex}
				tooltip={tile.name}
			/>
		</FrequentlyUsedTileVisualList.Item>
	);
};
Item.displayName = "Item";

const SkeletonItem = (): ReactElement => {
	return (
		<FrequentlyUsedTileVisualList.Item>
			<SkeletonFrequentlyUsedTileVisual />
		</FrequentlyUsedTileVisualList.Item>
	);
};
SkeletonItem.displayName = "SkeletonItem";